import React from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';

import { CategoriesForMenu } from 'shared/constants/products';
import { CategoriesMobileDisplayOption, CategoriesTypeOption, RadiusOption } from 'src/gql/graphql';

import { Category, ListProps } from 'src/components/categories-nav/categories-nav.types';
import { LargeList } from 'src/components/categories-nav/lists/large-list';
import { CompactList } from 'src/components/categories-nav/lists/compact-list';
import { StackedList } from 'src/components/categories-nav/lists/stacked-list';
import { TextList } from 'src/components/categories-nav/lists/text-list';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import { useHardAgeGate } from 'hooks/use-hard-age-gate';
import { tracker } from 'src/utils/analytics';

import { AssetPickerField } from '../fields/widgets/asset-picker-field';
import { selectField } from '../fields/widgets/select-field';
import { toggleField } from '../fields/widgets/toggle-field';
import { interpolateFromPuck } from '../interpolation';

/**
 * An **author-chosen** carousel of navigation tiles: which categories or collections
 * appear, and in what order.
 *
 * **Not a Product Carousel type, deliberately.** Every type in that block is a carousel OF PRODUCTS
 * with a different filter -- `CategoryCarousel` calls `useDispensaryProducts({ filters: { category } })`
 * and renders `<ProductCarousel>`, so "Popular Category" means products *within* a category. Here
 * the items ARE the categories: navigation tiles with a destination each, not products with an
 * add-to-cart. Adding this as a Product Carousel type would put a non-product carousel inside a
 * block whose every other type returns products and whose heading and `isConfigured` machinery
 * assumes it.
 *
 * **Complements `categoriesNavConfig`, which it must not replace.** That block is `fields: {}` /
 * `defaultProps: {}`, so it mirrors whatever categories the store has. This is the third
 * store-mirror-versus-authored pair in this codebase (Image Banner Rotation / Slideshow, Categories /
 * this Carousel, and the Text Banner's store fallback), so it is an established pattern rather than a
 * coincidence.
 *
 * The palette label names the authored destination behavior so authors do not confuse this block
 * with Product Carousel, whose tiles are products rather than links.
 *
 * Items come from `useDispensaryCategories`, which already returns categories and collections in one
 * shape (`{ key, label, value, slug?, customCategory? }`), already access-filtered and store-ordered.
 * Reusing that rather than querying separately means an author cannot select a collection a shopper
 * is not entitled to see: anything the hook withholds is simply not available to render.
 */

/** One row of the items array. Puck's array field stores objects, not bare strings. */
export type NavItem = {
  value: string;
  image?: string;
};

export type NavCarouselProps = {
  heading: string;
  showHeading: boolean;
  itemType: string;
  items: NavItem[];
  type: CategoriesTypeOption;
  style: RadiusOption;
  mobileDisplay: CategoriesMobileDisplayOption;
};

// Stable references for defaults.
const DEFAULT_CATEGORY_ITEMS: NavItem[] = CategoriesForMenu.map(({ value }) => ({ value }));

/**
 * Subcategories are deliberately absent, and it is not an oversight.
 *
 * Tiles are resolved against `useDispensaryCategories`, which returns only top-level categories and
 * collections: it maps its filter data with `.map(({ category }) => category)`
 * (`use-dispensary-categories.js:114`), discarding the subcategory. `CategoryTile`'s destination
 * helper `useGetCustomCategoryLink(customCategory, slug, key)` has no subcategory parameter either,
 * so a subcategory tile would have nothing to resolve and nowhere to link.
 *
 * Offering it would have let an author pick valid-looking subcategories, save, and get a permanently
 * empty carousel -- a dead option is worse than an absent one. Adding it needs a resolution source
 * and a destination for subcategory tiles, which is a separate piece of work.
 */
const ITEM_TYPE_OPTIONS = [
  { label: 'Categories', value: 'categories' },
  { label: 'Collections', value: 'collections' },
] as const;

const TYPE_OPTIONS = [
  { label: 'Large', value: CategoriesTypeOption.large },
  { label: 'Compact', value: CategoriesTypeOption.compact },
  { label: 'Stacked', value: CategoriesTypeOption.stacked },
  { label: 'Text', value: CategoriesTypeOption.text },
] as const;

const STYLE_OPTIONS = [
  { label: 'Rounded Rectangle', value: RadiusOption.roundedRectangle },
  { label: 'Rounded', value: RadiusOption.rounded },
  { label: 'Square', value: RadiusOption.square },
] as const;

const MOBILE_DISPLAY_OPTIONS = [
  { label: 'Grid', value: CategoriesMobileDisplayOption.grid },
  { label: 'Row', value: CategoriesMobileDisplayOption.row },
] as const;

/**
 * The option list for a given item type.
 *
 * Categories come from the canonical static constants, so the CMS cannot drift from the menu's own
 * vocabulary. Subcategories are deliberately excluded here -- see the note above `ITEM_TYPE_OPTIONS`.
 * Collections are per-store live data, so they are not offered here: the picker below resolves them
 * from the dispensary at render time instead.
 */
export function itemOptionsFor(itemType: string): { label: string; value: string }[] {
  if (itemType === 'categories') {
    return CategoriesForMenu.map(({ label, value }) => ({ label, value }));
  }
  return [];
}

/**
 * Returns valid NavItem rows for the given item type in author's order, preserving per-category
 * image overrides.
 */
export function validNavItems(itemType: string, items: unknown): NavItem[] {
  if (!Array.isArray(items)) {
    return [];
  }
  const seen = new Set<string>();
  const rows = items.reduce<NavItem[]>((acc, item) => {
    const value = typeof item === 'string' ? item : (item as NavItem | undefined)?.value;
    const image =
      typeof item === 'object' && item !== null && typeof (item as NavItem).image === 'string'
        ? (item as NavItem).image
        : undefined;
    if (typeof value === 'string' && value !== '' && !seen.has(value)) {
      seen.add(value);
      acc.push(image ? { value, image } : { value });
    }
    return acc;
  }, []);
  if (itemType === 'collections') {
    return rows;
  }
  const allowed = new Set(itemOptionsFor(itemType).map((option) => option.value));
  return rows.filter((row) => allowed.has(row.value));
}

/**
 * Keeps only stored item values that are valid for the current item type, in the author's order.
 *
 * Saved page data is unvalidated JSON, and switching item type leaves the previous type's values
 * behind -- a collection slug must not survive a switch to Categories. Same class of staleness as
 * `ProductCarousel`'s subcategory, handled the same way: drop rather than render something the
 * author is no longer asserting.
 */
export function validItems(itemType: string, items: unknown): string[] {
  return validNavItems(itemType, items).map((item) => item.value);
}

type ResolvedCategory = {
  key?: string;
  label?: string;
  value?: string;
  slug?: string;
  customCategory?: boolean;
  isOutlet?: boolean;
  cardImage?: string;
};

/**
 * Matches an author's stored value against the store's resolved list.
 *
 * Categories are matched on `value`, collections on `slug` -- the same identifiers the CMS store
 * elsewhere uses, and the reason a collection's slug rather than its UUID is what gets saved.
 */
function findResolved(stored: string, resolved: ResolvedCategory[], itemType: string): ResolvedCategory | undefined {
  if (itemType === 'collections') {
    return resolved.find((category) => category.customCategory === true && category.slug === stored);
  }
  return resolved.find((category) => category.customCategory !== true && category.value === stored);
}

export const getListComponent = (type: CategoriesTypeOption): ((props: ListProps) => JSX.Element) => {
  switch (type) {
    case CategoriesTypeOption.text:
      return TextList;
    case CategoriesTypeOption.compact:
      return CompactList;
    case CategoriesTypeOption.stacked:
      return StackedList;
    case CategoriesTypeOption.large:
    default:
      return LargeList;
  }
};

function NavCarouselRender({
  heading = 'Shop by category',
  showHeading = true,
  itemType = 'categories',
  items = DEFAULT_CATEGORY_ITEMS,
  type = CategoriesTypeOption.large,
  style = RadiusOption.roundedRectangle,
  mobileDisplay = CategoriesMobileDisplayOption.grid,
  puck,
}: NavCarouselProps & { puck: { isEditing: boolean; metadata?: Record<string, unknown> } }): JSX.Element {
  const { dispensary } = useDispensary();
  // Typed as required by the hook's .d.ts, but it really can arrive undefined -- the published
  // renderer has no error boundary, so this guard is load-bearing rather than defensive.
  const { categories } = useDispensaryCategories() as { categories?: ResolvedCategory[] };
  const { hardAgeGate, dispensaryLogoImage } = useHardAgeGate();

  const chosen = validNavItems(itemType, items);
  // Only a literal `false` hides the heading; any other stored value shows it.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare -- runtime may not be boolean
  const resolvedTitle = showHeading === false ? '' : interpolateFromPuck(heading, puck);

  // CategoryTile reads the dispensary for its photo, and useDispensaryCategories needs one to
  // resolve collections at all, so neither can render before the dispensary loads.
  if (!dispensary) {
    return <></>;
  }

  if (itemType === '' || chosen.length === 0) {
    if (!puck.isEditing) {
      return <></>;
    }
    return (
      <div
        data-testid='cms-nav-carousel-placeholder'
        style={{
          padding: 24,
          border: '1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd)',
          borderRadius: 'var(--dcm-cms-placeholder-radius, 8px)',
          background: 'var(--dcm-cms-placeholder-bg, #f0f2f4)',
          color: 'var(--dcm-cms-placeholder-color, #646d72)',
          fontSize: 14,
        }}
      >
        {itemType === '' ? 'Choose what this carousel shows in the settings panel.' : 'Choose at least one item.'}
      </div>
    );
  }

  // Author order is the point of this block, so the stored list drives iteration -- not the store's
  // own ordering, which `useDispensaryCategories` has already applied to `categories`.
  const resolvedCategories = chosen.reduce<Category[]>((acc, item) => {
    const resolved = findResolved(item.value, categories ?? [], itemType);
    // An item the store does not have (removed category, collection on another store, or one this
    // shopper cannot see) is skipped rather than rendered as a dead tile.
    if (resolved) {
      acc.push({
        key: resolved.key ?? item.value,
        label: resolved.label ?? item.value,
        value: resolved.value ?? item.value,
        slug: resolved.slug,
        customCategory: resolved.customCategory,
        isOutlet: resolved.isOutlet,
        cardImage: hardAgeGate ? dispensaryLogoImage : item.image ?? resolved.cardImage,
        suppressImage: hardAgeGate && !dispensaryLogoImage,
        onClick: () =>
          tracker.gaGTMClickEvent({
            eventCategory: 'navigation click',
            eventAction: 'category tile',
            eventLabel: resolved.label ?? item.value,
          }),
      });
    }
    return acc;
  }, []);

  if (resolvedCategories.length === 0) {
    if (!puck.isEditing) {
      return <></>;
    }
    return (
      <div
        data-testid='cms-nav-carousel-empty'
        style={{ padding: 24, fontSize: 14, color: 'var(--dcm-cms-placeholder-color, #646d72)' }}
      >
        None of the chosen items exist for this store, so nothing will appear on the published page.
      </div>
    );
  }

  const ListComponent = getListComponent(type);

  return (
    <div
      data-testid='cms-nav-carousel'
      style={
        {
          '--dcm-cms-nav-carousel-heading-color':
            'var(--dcm-color-text-primary, var(--dcm-section-title-color, #485055))',
        } as React.CSSProperties
      }
      data-order={chosen.map((item) => item.value).join(',')}
      data-type={type}
      data-style={style}
      data-mobile-display={mobileDisplay}
    >
      <ListComponent
        categories={resolvedCategories}
        mobileDisplay={mobileDisplay}
        style={style}
        heading={resolvedTitle}
      />
    </div>
  );
}

export const navCarouselConfig: ComponentConfig<NavCarouselProps> = {
  label: 'Link Carousel',
  ai: {
    instructions:
      'A carousel of navigation tiles the AUTHOR chooses: categories or collections, in a chosen order. Not the same as "Categories", which shows every category the store has and cannot be edited from a page. Not a product carousel either -- use Product Carousel when the items should be products.',
  },
  fields: {
    heading: {
      type: 'text',
      label: 'Heading',
      ai: { instructions: 'Optional title above the carousel. Supports {{name}} and {{address}} placeholders.' },
    },
    showHeading: toggleField(
      'Show heading',
      'Set to false to render the carousel with no heading at all, which is different from leaving the Heading field empty.',
      undefined,
      { defaultOn: true }
    ),
    type: selectField('Type', TYPE_OPTIONS, 'Size and card layout of navigation tiles.'),
    style: selectField('Style', STYLE_OPTIONS, 'Corner radius of navigation tiles.'),
    mobileDisplay: selectField('Display (mobile only)', MOBILE_DISPLAY_OPTIONS, 'Mobile layout for navigation tiles.'),
    itemType: {
      type: 'radio',
      label: 'Items to show',
      options: [...ITEM_TYPE_OPTIONS],
      ai: {
        schema: { type: 'string', enum: ITEM_TYPE_OPTIONS.map(({ value }) => value) },
        instructions: 'What kind of thing the tiles link to. Everything else follows from this, so always set it.',
      },
    },
    // Rebuilt per item type by resolveFields; the static entry exists to satisfy TypeScript.
    items: {
      type: 'custom',
      label: 'Items',
      render: () => <></>,
      ai: { exclude: true },
    },
  },
  defaultProps: {
    heading: 'Shop by category',
    showHeading: true,
    itemType: 'categories',
    items: DEFAULT_CATEGORY_ITEMS,
    type: CategoriesTypeOption.large,
    style: RadiusOption.roundedRectangle,
    mobileDisplay: CategoriesMobileDisplayOption.grid,
  },
  /**
   * Drops stored items that are not valid for the current item type, AND normalizes bare strings to
   * `{ value }` rows -- both are `resolveData`'s job because `resolveFields` rebuilds the item
   * *field* when the type changes but cannot touch stored *props*.
   *
   * Category carousels begin with all applicable categories by default; collection carousels begin empty.
   * Switching type resets items accordingly.
   */
  resolveData: ({ props }, { changed, lastData }) => {
    const previousItemType = lastData ? (lastData.props as { itemType?: string }).itemType : undefined;
    const isTypeSwitch = Boolean(lastData) && changed.itemType === true && previousItemType !== props.itemType;

    let kept: NavItem[];
    if (isTypeSwitch) {
      kept = props.itemType === 'categories' ? CategoriesForMenu.map(({ value }) => ({ value })) : [];
    } else {
      kept = validNavItems(props.itemType, props.items);
    }

    const inputLength = Array.isArray(props.items) ? props.items.length : 0;
    const needsReshape =
      Array.isArray(props.items) &&
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime disagrees with declared type
      !props.items.every((item) => typeof item === 'object' && item !== null && 'value' in item);

    const needsItemsUpdate = kept.length !== inputLength || needsReshape || isTypeSwitch;

    if (!needsItemsUpdate) {
      return { props };
    }
    return { props: { ...props, items: kept } };
  },
  resolveFields: (data, { fields }) => {
    const { items: _items, ...baseFields } = fields;
    const { itemType } = data.props;
    if (itemType === '') {
      return baseFields as Fields<NavCarouselProps>;
    }
    const isCategories = itemType === 'categories';
    return ({
      heading: baseFields.heading,
      showHeading: baseFields.showHeading,
      type: baseFields.type,
      style: baseFields.style,
      mobileDisplay: baseFields.mobileDisplay,
      itemType: baseFields.itemType,
      items: {
        type: 'array',
        label: isCategories ? 'Categories (in order)' : 'Collections (in order)',
        // Reordering is Puck's own array control, which is also what makes the author's order the
        // stored order.
        getItemSummary: (row: NavItem, index?: number) => {
          if (!row.value) return `Item ${(index ?? 0) + 1}`;
          if (isCategories) {
            const match = CategoriesForMenu.find((c) => c.value === row.value);
            return match?.label ?? row.value;
          }
          return row.value;
        },
        defaultItemProps: { value: '', ...(isCategories ? { image: '' } : {}) },
        arrayFields: {
          value:
            itemType === 'collections'
              ? { type: 'text', label: 'Collection slug' }
              : selectField(
                  'Category',
                  [{ label: 'Select…', value: '' }, ...itemOptionsFor(itemType)],
                  'Which item this tile links to.'
                ),
          ...(isCategories
            ? {
                image: {
                  type: 'custom',
                  label: 'Image override (optional)',
                  render: AssetPickerField,
                  ai: {
                    schema: { type: 'string' },
                    stream: false,
                    instructions: 'Optional image override URL. Uses store category image if blank.',
                  },
                },
              }
            : {}),
        },
      },
    } as unknown) as Fields<NavCarouselProps>;
  },
  render: NavCarouselRender,
};
